/**
 * Work Order detail — Phase 2.
 * Tabs: Overview · Milestones/Deliverables · Variation Orders ·
 * Financial Summary · Documents · Audit Log
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Send, CheckCircle2, XCircle, Play, Ban, Lock, Plus, Banknote, FilePen, BellRing,
} from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';
import { formatINR, formatDate } from '../../lib/format';
import { WO_STATUS_BADGE, ctLabel } from './WorkOrders';
import WOMeasurementTab from './WOMeasurementTab';
import WOBillingTab from './WOBillingTab';
import WOHireTab from './WOHireTab';
import WOAmcTab from './WOAmcTab';
import { useUrlTab } from '../../lib/hooks/useUrlState';

export default function WorkOrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [wo, setWo] = useState(null);
  const [fin, setFin] = useState(null);
  const [tab, setTab] = useUrlTab('tab', 'overview');
  const [voForm, setVoForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    apiGet(`/work-orders/${id}`).then(setWo).catch(() => toast.error('Failed to load work order'));
    apiGet(`/work-orders/${id}/financial-summary`).then(setFin).catch(() => {});
  }, [id]);
  useEffect(() => { reload(); }, [reload]);

  const act = async (path, body, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      await apiPost(`/work-orders/${id}/${path}`, body || {});
      toast.success('Done');
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  if (!wo) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-slate-400" /></div>;

  const hasMilestones = ['lump_sum', 'supply_erection'].includes(wo.contract_type);
  const hasDeliverables = wo.contract_type === 'consultancy';
  const hasMB = ['measured_works', 'supply_erection'].includes(wo.contract_type);
  const isHire = wo.contract_type === 'equipment_hire';
  const isAmc = wo.contract_type === 'amc_recurring';
  const tabs = [
    ['overview', 'Overview'],
    ...(hasMilestones ? [['milestones', 'Milestones']] : []),
    ...(hasDeliverables ? [['deliverables', 'Deliverables']] : []),
    ...(hasMB ? [['mb', 'Measurement Books']] : []),
    ...(isHire ? [['hire', 'Hire Logs']] : []),
    ...(isAmc ? [['amc', 'AMC Confirmations']] : []),
    ['billing', 'WPC & Billing'],
    ['vos', `Variation Orders (${(wo.variation_orders || []).length})`],
    ['financial', 'Financial Summary'],
    ['documents', 'Documents'],
    ['audit', 'Audit Log'],
  ];

  // Phase 7 — AMC renewal banner
  let renewalDays = null;
  if (isAmc && wo.end_date && ['approved', 'active'].includes(wo.status)) {
    const diff = Math.ceil((new Date(wo.end_date) - new Date()) / 86400000);
    if (diff <= 60) renewalDays = diff;
  }

  const certifyMilestone = async (m) => {
    if (!window.confirm(`Certify "${m.title}" (${m.billing_pct}% = ${formatINR((wo.contract_value || 0) * (m.billing_pct || 0) / 100)}) and raise its WPC?`)) return;
    setBusy(true);
    try {
      await apiPost(`/work-orders/${id}/milestones/${m.id}/certify`, {});
      toast.success('Milestone certified — WPC raised');
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const amend = async () => {
    const field = window.prompt("Field to amend ('contract_value', 'end_date' or 'retention_pct'):", 'contract_value');
    if (!field) return;
    const newValue = window.prompt(`New value for ${field}:`);
    if (newValue === null || newValue === '') return;
    const reason = window.prompt('Amendment reason (required):');
    if (!reason) return;
    setBusy(true);
    try {
      const out = await apiPost(`/work-orders/${id}/amend`, {
        field, new_value: field === 'end_date' ? newValue : Number(newValue), reason,
      });
      toast.success(out.status === 'pending_approval'
        ? 'Amended — value increase requires re-approval'
        : 'Amendment recorded');
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="wo-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button onClick={() => nav('/app/procurement/work-orders')} className="flex items-center gap-1 text-xs text-slate-500 hover:text-[#3b4872] font-display" data-testid="wo-back-btn">
            <ArrowLeft size={12} /> Work Orders
          </button>
          <h1 className="font-display font-bold text-xl text-slate-900 mt-1 flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm text-[#3b4872]">{wo.wo_no}</span> {wo.title}
            <span className={`px-2 py-0.5 text-[10px] rounded-sm font-display font-bold uppercase ${WO_STATUS_BADGE[wo.status] || ''}`} data-testid="wo-status-badge">{(wo.status || '').replace('_', ' ')}</span>
          </h1>
          <div className="text-xs text-slate-500 mt-1">
            {ctLabel(wo.contract_type)} · {wo.vendor_name || 'No vendor'} · {wo.project_name || 'No project'} · {formatINR(wo.contract_value)}
            {wo.approval_tier && <> · Tier: {wo.approval_tier}</>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {wo.status === 'draft' && (
            <ActionBtn icon={Send} onClick={() => act('submit')} disabled={busy} testid="wo-submit-btn">Submit for Approval</ActionBtn>
          )}
          {wo.status === 'pending_approval' && (
            <>
              <ActionBtn icon={CheckCircle2} onClick={() => act('approve')} disabled={busy} green testid="wo-approve-btn">Approve</ActionBtn>
              <ActionBtn icon={XCircle} onClick={() => { const r = window.prompt('Rejection reason:'); if (r) act('reject', { reason: r }); }} disabled={busy} red testid="wo-reject-btn">Reject</ActionBtn>
            </>
          )}
          {wo.status === 'approved' && (
            <ActionBtn icon={Play} onClick={() => act('activate')} disabled={busy} green testid="wo-activate-btn">Activate</ActionBtn>
          )}
          {['approved', 'active'].includes(wo.status) && (
            <>
              <ActionBtn icon={FilePen} onClick={amend} disabled={busy} testid="wo-amend-btn">Amend</ActionBtn>
              <ActionBtn icon={Banknote} onClick={() => {
                const amt = window.prompt('Retention amount to release (₹):');
                if (!amt) return;
                const reason = window.prompt('Reason (e.g. DLP completed):') || '';
                act('release-retention', { amount: Number(amt), reason });
              }} disabled={busy} testid="wo-release-retention-btn">Release Retention</ActionBtn>
              <ActionBtn icon={Ban} onClick={() => { const r = window.prompt('Termination reason:'); if (r) act('terminate', { reason: r }); }} disabled={busy} red testid="wo-terminate-btn">Terminate</ActionBtn>
            </>
          )}
          {['completed', 'terminated', 'active'].includes(wo.status) && (
            <ActionBtn icon={Lock} onClick={() => act('close', null, 'Close this WO? Final account must be settled.')} disabled={busy} testid="wo-close-btn">Close</ActionBtn>
          )}
        </div>
      </div>

      {/* Phase 7 — AMC renewal alert */}
      {renewalDays !== null && (
        <div className={`px-3 py-2 border rounded-sm text-xs flex items-center gap-2 ${renewalDays < 0 ? 'bg-red-50 border-red-200 text-red-700' : 'bg-amber-50 border-amber-200 text-amber-800'}`} data-testid="amc-renewal-banner">
          <BellRing size={14} />
          {renewalDays < 0
            ? `This AMC expired ${Math.abs(renewalDays)} days ago — renew or close the contract.`
            : `This AMC expires in ${renewalDays} days (${formatDate(wo.end_date)}) — initiate renewal or re-tender.`}
        </div>
      )}

      {/* KPI strip */}
      {fin && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Kpi label="Revised Contract" value={formatINR(fin.revised_contract_value)} />
          <Kpi label="Certified" value={`${formatINR(fin.certified_to_date)} (${fin.certified_pct}%)`} />
          <Kpi label="Billed" value={formatINR(fin.billed_to_date)} />
          <Kpi label="Retention Held" value={formatINR(fin.retention_held)} />
          <Kpi label="Advance Balance" value={formatINR(fin.advance_balance)} />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {tabs.map(([tid, label]) => (
          <button key={tid} onClick={() => setTab(tid)}
            className={`px-3 py-2 text-xs font-display font-bold uppercase tracking-wide whitespace-nowrap border-b-2 -mb-px ${tab === tid ? 'border-[#3b4872] text-[#3b4872]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid={`wo-detail-tab-${tid}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-4">
        {tab === 'overview' && (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3" data-testid="wo-overview">
            <Field label="Contract Type" value={ctLabel(wo.contract_type)} />
            <Field label="Vendor" value={wo.vendor_name ? `${wo.vendor_code} · ${wo.vendor_name}` : '—'} />
            <Field label="Project" value={wo.project_name || '—'} />
            <Field label="Period" value={`${wo.start_date ? formatDate(wo.start_date) : '—'} → ${wo.end_date ? formatDate(wo.end_date) : '—'}`} />
            <Field label="Rate Basis" value={(wo.rate_basis || '').replace('_', ' ')} />
            <Field label="Payment Terms" value={wo.payment_terms || '—'} />
            <Field label="Retention" value={`${wo.retention_pct}%`} />
            <Field label="DLP" value={`${wo.dlp_months} months`} />
            <Field label="LD Clause" value={`${wo.ld_clause_pct}% / week`} />
            <Field label="Mobilisation Advance" value={formatINR(wo.mobilisation_advance)} />
            <Field label="Advance Recovery" value={`${wo.advance_recovery_pct}% per bill`} />
            <Field label="Security Deposit" value={wo.security_deposit_amount ? `${formatINR(wo.security_deposit_amount)} (${wo.security_deposit_mode || '—'})` : '—'} />
            <Field label="TDS Section" value={wo.tds_section || 'none'} />
            <Field label="Created By" value={`${wo.created_by} · ${formatDate(wo.created_at)}`} />
            {wo.approved_by && <Field label="Approved By" value={`${wo.approved_by} · ${formatDate(wo.approved_at)}`} />}
            {wo.contract_type === 'equipment_hire' && (
              <>
                <Field label="Equipment" value={wo.equipment_description || '—'} />
                <Field label="Hire Rate" value={`${formatINR(wo.hire_rate_per_hour)}/hr · idle ${formatINR(wo.idle_rate_per_hour)}/hr`} />
                <Field label="Min Guarantee" value={`${wo.min_guarantee_hours} hrs/month · fuel: ${wo.fuel_scope}`} />
              </>
            )}
            {wo.contract_type === 'amc_recurring' && (
              <>
                <Field label="AMC Monthly" value={formatINR(wo.amc_monthly_value)} />
                <Field label="AMC Period" value={`${wo.amc_period_months} months`} />
                <Field label="SLA Terms" value={wo.sla_terms || '—'} />
              </>
            )}
            {wo.scope_description && (
              <div className="col-span-full">
                <div className="kpi-overline">Scope</div>
                <p className="text-xs text-slate-700 mt-1 whitespace-pre-wrap">{wo.scope_description}</p>
              </div>
            )}
          </div>
        )}

        {tab === 'milestones' && (
          <MilestoneTable rows={wo.milestones || []} contractValue={wo.contract_value} onCertify={certifyMilestone} canCertify={['approved', 'active'].includes(wo.status)} />
        )}
        {tab === 'mb' && <WOMeasurementTab wo={wo} onChanged={reload} />}
        {tab === 'billing' && <WOBillingTab wo={wo} onChanged={reload} />}
        {tab === 'hire' && <WOHireTab wo={wo} onChanged={reload} />}
        {tab === 'amc' && <WOAmcTab wo={wo} onChanged={reload} />}
        {tab === 'deliverables' && (
          <table className="w-full dense-table" data-testid="wo-deliverables-table">
            <thead><tr><th>Deliverable</th><th className="text-right">% Billing</th><th>Submitted</th><th>Status</th></tr></thead>
            <tbody>
              {(wo.deliverables || []).length === 0 && <tr><td colSpan="4" className="text-center py-8 text-slate-400 text-xs">No deliverables defined</td></tr>}
              {(wo.deliverables || []).map((d) => (
                <tr key={d.id}>
                  <td className="text-xs font-display font-semibold">{d.title}</td>
                  <td className="text-right tabular-nums text-xs">{d.billing_pct}%</td>
                  <td className="text-xs">{d.submitted_date ? formatDate(d.submitted_date) : '—'}</td>
                  <td><span className="px-1.5 py-0.5 text-[10px] bg-slate-100 rounded-sm uppercase font-display">{d.acceptance_status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {tab === 'vos' && (
          <div data-testid="wo-vos">
            <table className="w-full dense-table">
              <thead><tr><th>VO No</th><th>Description</th><th className="text-right">Amount</th><th>Status</th><th>Approved By</th><th></th></tr></thead>
              <tbody>
                {(wo.variation_orders || []).length === 0 && <tr><td colSpan="6" className="text-center py-8 text-slate-400 text-xs">No variation orders</td></tr>}
                {(wo.variation_orders || []).map((vo) => (
                  <tr key={vo.id}>
                    <td className="font-mono text-xs">{vo.vo_no}</td>
                    <td className="text-xs max-w-[280px] truncate">{vo.description}</td>
                    <td className={`text-right tabular-nums text-xs font-display font-bold ${vo.amount < 0 ? 'text-red-600' : ''}`}>{formatINR(vo.amount)}</td>
                    <td><span className={`px-1.5 py-0.5 text-[10px] rounded-sm uppercase font-display font-bold ${vo.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : vo.status === 'rejected' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>{vo.status}</span></td>
                    <td className="text-xs">{vo.approved_by || '—'}</td>
                    <td>
                      {vo.status === 'pending' && (
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => act(`variation-order/${vo.id}/approve`)} className="px-2 py-1 text-[10px] bg-emerald-600 text-white rounded-sm font-display font-bold" data-testid={`wo-vo-approve-${vo.vo_no}`}>Approve</button>
                          <button onClick={() => act(`variation-order/${vo.id}/reject`)} className="px-2 py-1 text-[10px] bg-red-50 text-red-700 border border-red-200 rounded-sm font-display font-bold">Reject</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {['approved', 'active'].includes(wo.status) && (
              voForm ? (
                <div className="mt-3 border border-slate-200 rounded-sm p-3 bg-slate-50 space-y-2" data-testid="wo-vo-form">
                  <input placeholder="VO description" value={voForm.description || ''} onChange={(e) => setVoForm({ ...voForm, description: e.target.value })} className="w-full text-xs border border-slate-200 rounded-sm px-2 py-1.5" data-testid="wo-vo-desc" />
                  <input placeholder="Amount (negative = omission)" type="number" value={voForm.amount || ''} onChange={(e) => setVoForm({ ...voForm, amount: e.target.value })} className="w-full text-xs border border-slate-200 rounded-sm px-2 py-1.5" data-testid="wo-vo-amount" />
                  <div className="flex gap-2">
                    <button onClick={async () => { await act('variation-order', { description: voForm.description, amount: Number(voForm.amount) }); setVoForm(null); }} className="px-2.5 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold" data-testid="wo-vo-save">Add VO</button>
                    <button onClick={() => setVoForm(null)} className="px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-sm">Cancel</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setVoForm({})} className="mt-3 flex items-center gap-1 px-2.5 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold" data-testid="wo-add-vo-btn">
                  <Plus size={11} /> Add Variation Order
                </button>
              )
            )}
          </div>
        )}

        {tab === 'financial' && fin && (
          <div className="max-w-md space-y-1.5 text-sm" data-testid="wo-financial">
            <WaterfallRow label="Contract value" value={fin.contract_value} />
            <WaterfallRow label="+ Variation orders" value={fin.vos_total} />
            {fin.amendments_delta !== 0 && <WaterfallRow label="+ Amendments" value={fin.amendments_delta} />}
            <WaterfallRow label="= Revised contract" value={fin.revised_contract_value} bold />
            <WaterfallRow label={`− Certified to date (${fin.certified_pct}%)`} value={fin.certified_to_date} />
            <WaterfallRow label="= Balance to certify" value={fin.balance_to_certify} bold />
            <div className="border-t border-slate-200 my-2" />
            <WaterfallRow label="Billed to date" value={fin.billed_to_date} />
            <WaterfallRow label="Paid to date" value={fin.paid_to_date} />
            <WaterfallRow label="Retention held" value={fin.retention_held} />
            <WaterfallRow label="Advance balance" value={fin.advance_balance} />
            {(wo.retention_releases || []).length > 0 && (
              <>
                <div className="kpi-overline pt-2">Retention Releases</div>
                {(wo.retention_releases || []).map((r) => (
                  <div key={r.id} className="flex justify-between text-xs text-slate-600">
                    <span>{formatDate(r.release_date)} · {r.reason || '—'}</span>
                    <span className="tabular-nums">{formatINR(r.amount)}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {tab === 'documents' && (
          <div className="text-xs text-slate-500 py-6 text-center" data-testid="wo-documents">
            {(wo.attachments || []).length === 0 ? 'No documents attached. Signed WO copy and scope documents can be linked via Drive.' : (
              <ul className="text-left space-y-1">
                {wo.attachments.map((a, i) => <li key={i}><a href={a.url || a} target="_blank" rel="noreferrer" className="text-[#3b4872] underline">{a.name || a.url || a}</a></li>)}
              </ul>
            )}
          </div>
        )}

        {tab === 'audit' && (
          <div className="space-y-4" data-testid="wo-audit">
            <div>
              <div className="kpi-overline mb-2">Status History</div>
              <div className="space-y-2">
                {[...(wo.status_history || [])].reverse().map((h, i) => (
                  <div key={i} className="flex items-start gap-3 text-xs border-b border-slate-100 pb-2">
                    <span className="font-mono text-slate-400 whitespace-nowrap">{formatDate(h.at)}</span>
                    <span className="font-display font-bold uppercase text-[#3b4872]">{(h.event || '').replace(/_/g, ' ')}</span>
                    <span className="text-slate-600 flex-1">{h.detail}</span>
                    <span className="text-slate-500">{h.by}</span>
                  </div>
                ))}
              </div>
            </div>
            {(wo.amendments || []).length > 0 && (
              <div>
                <div className="kpi-overline mb-2">Amendments</div>
                <table className="w-full dense-table">
                  <thead><tr><th>Field</th><th>From</th><th>To</th><th>Reason</th><th>Re-approval</th><th>By</th></tr></thead>
                  <tbody>
                    {wo.amendments.map((a) => (
                      <tr key={a.id} data-testid={`wo-amendment-${a.id}`}>
                        <td className="text-xs font-display font-bold">{a.field}</td>
                        <td className="text-xs tabular-nums">{a.field === 'contract_value' ? formatINR(a.old_value) : String(a.old_value ?? '—')}</td>
                        <td className="text-xs tabular-nums">{a.field === 'contract_value' ? formatINR(a.new_value) : String(a.new_value)}</td>
                        <td className="text-xs">{a.reason}</td>
                        <td className="text-xs">{a.requires_reapproval ? <span className="text-amber-700 font-bold">Required</span> : '—'}</td>
                        <td className="text-xs text-slate-500">{a.amended_by} · {formatDate(a.amended_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <ProcAuditFeed woId={wo.id} />
          </div>
        )}
      </div>
    </div>
  );

  function MilestoneTable({ rows, contractValue, onCertify, canCertify }) {
    return (
      <table className="w-full dense-table" data-testid="wo-milestones-table">
        <thead><tr><th>Milestone</th><th className="text-right">% Billing</th><th className="text-right">Value</th><th>Planned</th><th>Actual</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-slate-400 text-xs">No milestones defined</td></tr>}
          {rows.map((m) => (
            <tr key={m.id}>
              <td className="text-xs font-display font-semibold">{m.title}</td>
              <td className="text-right tabular-nums text-xs">{m.billing_pct}%</td>
              <td className="text-right tabular-nums text-xs">{formatINR((contractValue || 0) * (m.billing_pct || 0) / 100)}</td>
              <td className="text-xs">{m.planned_date ? formatDate(m.planned_date) : '—'}</td>
              <td className="text-xs">{m.actual_date ? formatDate(m.actual_date) : '—'}</td>
              <td><span className={`px-1.5 py-0.5 text-[10px] rounded-sm uppercase font-display ${m.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100'}`}>{m.status}</span></td>
              <td>
                {canCertify && m.status === 'pending' && (
                  <button onClick={() => onCertify(m)} disabled={busy}
                    className="px-2 py-1 text-[10px] bg-[#3b4872] text-white rounded-sm font-display font-bold disabled:opacity-50"
                    data-testid={`wo-milestone-certify-${m.id}`}>
                    Certify → WPC
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
}

function ProcAuditFeed({ woId }) {
  const [rows, setRows] = React.useState(null);
  React.useEffect(() => {
    apiGet('/procurement/audit-trail', { doc_type: 'work_order', doc_id: woId })
      .then((d) => setRows(d?.items || [])).catch(() => setRows([]));
  }, [woId]);
  if (!rows || rows.length === 0) return null;
  return (
    <div data-testid="wo-proc-audit-feed">
      <div className="kpi-overline mb-2">Procurement Audit Trail</div>
      <div className="space-y-2">
        {rows.map((e) => (
          <div key={e.id} className="flex items-start gap-3 text-xs border-b border-slate-100 pb-2">
            <span className="font-mono text-slate-400 whitespace-nowrap">{formatDate(e.performed_at)}</span>
            <span className="font-display font-bold uppercase text-[#3b4872]">{(e.event_type || '').replace(/_/g, ' ')}</span>
            <span className="text-slate-600 flex-1">{e.amount != null ? formatINR(e.amount) : ''}</span>
            <span className="text-slate-500">{e.performed_by}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const ActionBtn = ({ icon: Icon, onClick, disabled, children, green, red, testid }) => (
  <button onClick={onClick} disabled={disabled}
    className={`flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm font-display font-bold uppercase tracking-wider disabled:opacity-50 ${
      green ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
        : red ? 'bg-red-50 hover:bg-red-100 text-red-700 border border-red-200'
          : 'bg-[#3b4872] hover:bg-[#2d3856] text-white'}`}
    data-testid={testid}>
    <Icon size={12} /> {children}
  </button>
);

const Kpi = ({ label, value }) => (
  <div className="bg-white border border-slate-200 rounded-sm px-3 py-2.5">
    <div className="kpi-overline">{label}</div>
    <div className="font-display font-bold text-sm text-slate-900 mt-0.5">{value}</div>
  </div>
);

const Field = ({ label, value }) => (
  <div>
    <div className="kpi-overline">{label}</div>
    <div className="text-xs text-slate-800 mt-0.5">{value}</div>
  </div>
);

const WaterfallRow = ({ label, value, bold }) => (
  <div className={`flex justify-between ${bold ? 'font-display font-bold' : ''}`}>
    <span className="text-xs text-slate-600">{label}</span>
    <span className="text-xs tabular-nums">{formatINR(value)}</span>
  </div>
);
